/**
 * PackingShelfQRSection - Packing shelf QR code generation
 *
 * Provides UI to generate, preview, print and download the packing shelf QR code.
 * Single-line orders bypass bins and go directly to the packing shelf.
 * This QR is scanned at the packing station to identify ready-to-pack orders.
 *
 * Uses existing `bins.generateShelfQR` backend endpoint.
 *
 * UX Requirements:
 * - Large touch targets (44px minimum)
 * - Lazy loading: QR only generated when dialog opens
 * - Print using shared QR print layout (70mm QR, one per page)
 * - Download option for manual printing
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { Printer, Package, Loader2, Download, Eye, MapPin } from 'lucide-react';
import { generatePrintHTML } from '../qr-print-utils';
import { printHtmlViaIframe } from '@/lib/print-utils';

export function PackingShelfQRSection() {
  const { t } = useTranslation('settings');
  const { selectedLocation } = useLocation();
  const { toast } = useToast();
  const [previewOpen, setPreviewOpen] = useState(false);

  // Query for shelf QR - only fetch when preview dialog is open
  const shelfQRQuery = trpc.bins.generateShelfQR.useQuery(
    { locationId: selectedLocation?.id ?? '' },
    {
      enabled: previewOpen && !!selectedLocation?.id,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    }
  );

  const handlePrint = () => {
    if (!shelfQRQuery.data?.qrDataUrl) {
      toast({
        title: t('production.shelfQr.qrGenerateFailed'),
        variant: 'destructive',
      });
      return;
    }

    const qrCodes = [{
      label: 'PACKING SHELF',
      qrDataUrl: shelfQRQuery.data.qrDataUrl,
    }];

    const printHTML = generatePrintHTML(qrCodes, 'SHELF', 'SHELF');
    printHtmlViaIframe(printHTML);
  };

  const handleDownload = () => {
    if (!shelfQRQuery.data?.qrDataUrl) return;

    const link = document.createElement('a');
    link.href = shelfQRQuery.data.qrDataUrl;
    link.download = `packing-shelf-qr-${selectedLocation?.name || 'location'}.png`;
    link.click();
  };

  if (!selectedLocation) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center"
        role="status"
        aria-label="No location selected"
      >
        <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" aria-hidden="true" />
        <p className="text-muted-foreground mb-2">
          {t('production.shelfQr.selectLocation')}
        </p>
        <p className="text-sm text-muted-foreground">
          <kbd className="px-1.5 py-0.5 bg-muted rounded border text-xs">Tab</kbd> to navigation, then use{' '}
          <kbd className="px-1.5 py-0.5 bg-muted rounded border text-xs">Enter</kbd> to select a location
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h4 className="font-medium">{t('production.shelfQr.title')}</h4>
          <p className="text-sm text-muted-foreground mt-1">
            {t('production.shelfQr.description')}
          </p>
        </div>
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="shrink-0 h-12">
              <Eye className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('production.shelfQr.previewAndPrint')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('production.shelfQr.title')}</DialogTitle>
              <DialogDescription>{selectedLocation.name}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-4">
              {shelfQRQuery.isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  {t('production.shelfQr.generatingQr')}
                </div>
              ) : shelfQRQuery.error ? (
                <div className="text-destructive text-center">
                  {t('production.shelfQr.qrGenerateFailed')}
                </div>
              ) : shelfQRQuery.data?.qrDataUrl ? (
                <>
                  <div className="border rounded-lg p-4 bg-white">
                    <img
                      src={shelfQRQuery.data.qrDataUrl}
                      alt="Packing Shelf QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    {t('production.shelfQr.qrLinksToLocation')}
                  </p>
                </>
              ) : null}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={!shelfQRQuery.data?.qrDataUrl}
                className="h-12"
              >
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('production.shelfQr.download', 'Download')}
              </Button>
              <Button
                onClick={handlePrint}
                disabled={shelfQRQuery.isLoading || !shelfQRQuery.data?.qrDataUrl}
                className="h-12"
              >
                <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('production.shelfQr.print', 'Print')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 text-sm">
        <h5 className="font-medium mb-2 flex items-center gap-2">
          <Package className="h-4 w-4" aria-hidden="true" />
          {t('production.shelfQr.usageInstructions')}
        </h5>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>{t('production.shelfQr.usageStep1')}</li>
          <li>{t('production.shelfQr.usageStep2')}</li>
          <li>{t('production.shelfQr.usageStep3')}</li>
          <li>{t('production.shelfQr.usageStep4')}</li>
        </ul>
      </div>
    </div>
  );
}
